import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PrinterIcon, SearchIcon } from 'lucide-react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useAdminCases, useAdminOverview } from '@/hooks/useAdmin';
import { AdminCaseRow, CaseQuery } from '@/schema/admin';
import { Status } from '@/schema/jawsScan';
import { Column, DataTable } from '../components/DataTable';
import { PipelineBar } from '../components/PipelineBar';
import { StatusBadge } from '../components/StatusBadge';
import { SectionCard } from '../components/SectionCard';
import { PrintModelsDialog } from '../features/PrintModelsDialog';
import { useAdminCaseSelection } from '../hooks/useAdminCaseSelection';
import { formatDate, formatDayCount } from '../lib/format';

const PAGE_SIZE = 15;
const STUCK_THRESHOLD_DAYS = 7;

/**
 * Cases across every clinic, ordered by how long they have been waiting. The filters live
 * in the URL so a specific view (say "everything stuck in Segmented") can be shared.
 */
export function AdminPipeline() {
  useDocumentTitle('Admin - Pipeline');
  const [searchParams, setSearchParams] = useSearchParams();
  const { patientId: openPatientId, scanId: openScanId, openCase } = useAdminCaseSelection();
  const [page, setPage] = useState(0);
  const [searchDraft, setSearchDraft] = useState(searchParams.get('search') ?? '');
  const [printRow, setPrintRow] = useState<AdminCaseRow | null>(null);

  const { data: overview } = useAdminOverview();

  const query: CaseQuery = useMemo(
    () => ({
      clinicId: searchParams.get('clinicId') || undefined,
      status: (searchParams.get('status') as Status) || undefined,
      search: searchParams.get('search') || undefined,
      minDaysInStage: searchParams.get('stuck') === '1' ? STUCK_THRESHOLD_DAYS : undefined,
      openOnly: searchParams.get('open') !== '0',
    }),
    [searchParams],
  );

  const { data, isLoading } = useAdminCases(page, PAGE_SIZE, query);

  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null) next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const columns: Column<AdminCaseRow>[] = [
    {
      key: 'patient',
      header: 'Patient',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{row.patientName}</p>
          <p className="truncate text-xs text-white/45">{row.orthodontistName}</p>
        </div>
      ),
    },
    {
      key: 'clinic',
      header: 'Clinic',
      render: (row) => <span className="text-sm text-white/80">{row.clinicName}</span>,
    },
    {
      key: 'status',
      header: 'Stage',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'waiting',
      header: 'Waiting',
      render: (row) => (
        <span
          className={cn(
            'text-sm',
            row.daysInStage >= STUCK_THRESHOLD_DAYS ? 'font-semibold text-amber-300' : 'text-white/70',
          )}
        >
          {formatDayCount(row.daysInStage)}
        </span>
      ),
    },
    {
      key: 'scanDate',
      header: 'Scan date',
      render: (row) => <span className="text-sm text-white/70">{formatDate(row.scanDate)}</span>,
    },
    {
      key: 'print',
      header: '',
      className: 'w-px',
      render: (row) => (
        <Button
          size="sm"
          variant={row.status === Status.printing ? 'ghost-light' : 'ghost-primary'}
          // The row itself opens the case; this cell is a different destination.
          onClick={(event) => {
            event.stopPropagation();
            setPrintRow(row);
          }}
        >
          <PrinterIcon />
          {row.status === Status.printing ? 'Printing' : 'Print'}
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title="Pipeline" description="Click a stage to filter the list below.">
        <PipelineBar
          stages={overview?.pipeline ?? []}
          activeStatus={query.status ?? null}
          onSelect={(status) => patchParams({ status: status ? String(status) : null })}
        />
      </SectionCard>

      <SectionCard
        title="Cases"
        description={
          data ? `${data.totalElements} case${data.totalElements === 1 ? '' : 's'} match` : undefined
        }
      >
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <div className="relative min-w-56 flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
            <Input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') patchParams({ search: searchDraft || null });
              }}
              onBlur={() => patchParams({ search: searchDraft || null })}
              placeholder="Filter by patient name"
              className="pl-9"
            />
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
            <Checkbox
              checked={query.minDaysInStage !== undefined}
              onCheckedChange={(checked) => patchParams({ stuck: checked ? '1' : null })}
            />
            Stuck over {STUCK_THRESHOLD_DAYS} days
          </label>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
            <Checkbox
              checked={Boolean(query.openOnly)}
              onCheckedChange={(checked) => patchParams({ open: checked ? null : '0' })}
            />
            Hide printed
          </label>
        </div>

        <DataTable
          columns={columns}
          rows={data?.content ?? []}
          rowKey={(row) => row.scanId}
          onRowClick={(row) => openCase(row.patientId, row.scanId)}
          isRowActive={(row) => row.patientId === openPatientId && row.scanId === openScanId}
          page={page}
          totalPages={data?.totalPages ?? 0}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No cases match these filters."
        />
      </SectionCard>

      {printRow && <PrintModelsDialog row={printRow} onClose={() => setPrintRow(null)} />}
    </div>
  );
}

export default AdminPipeline;
